import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from "../../Context/auth";
import Swal from 'sweetalert2';

const UFs = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

function EmpresaForm({ initialData = {}, onSubmit, isEditing = false, loading = false }) {
    const { currentUser } = useAuth();

    const [tipo_pessoa, setTipoPessoa] = useState(initialData.tipo_pessoa || 'fisica');
    const [nome, setNome] = useState(initialData.nome || '');
    const [nome_fantasia, setNomeFantasia] = useState(initialData.nome_fantasia || '');
    const [cpf_cnpj, setCpfCnpj] = useState(initialData.cpf_cnpj || '');
    const [email, setEmail] = useState(initialData.email || '');
    const [telefone, setTelefone] = useState(initialData.telefone || '');
    const [cep, setCep] = useState(initialData.cep || '');
    const [uf, setUf] = useState(initialData.uf || '');
    const [cidade, setCidade] = useState(initialData.cidade || '');
    const [rua, setRua] = useState(initialData.rua || '');
    const [numero, setNumero] = useState(initialData.numero || '');
    const [bairro, setBairro] = useState(initialData.bairro || '');
    const [complemento, setComplemento] = useState(initialData.complemento || '');
    const [is_cliente, setIsCliente] = useState(initialData.is_cliente ?? true);
    const [is_fornecedor, setIsFornecedor] = useState(initialData.is_fornecedor || false);
    const [is_transportadora, setIsTransportadora] = useState(initialData.is_transportadora || false);
    const [is_terceiro, setIsTerceiro] = useState(initialData.is_terceiro || false);
    const [transportadora_padrao_id, setTransportadoraPadraoId] = useState(initialData.transportadora_padrao_id || '');

    const [cidades, setCidades] = useState([]);
    const [transportadoras, setTransportadoras] = useState([]);

    const buscarCidadesPorUF = useCallback(async (ufValue) => {
        if (!ufValue || !ufValue.trim()) { setCidades([]); return []; }
        try {
            const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${ufValue}/municipios`);
            const data = await response.json();
            const listaCidades = data.map(m => m.nome).sort();
            setCidades(listaCidades);
            return listaCidades;
        } catch (error) {
            setCidades([]);
            return [];
        }
    }, []);

    const buscarCEP = async (cepValue) => {
        const cepLimpo = cepValue.replace(/\D/g, '');
        if (cepLimpo.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
                const data = await response.json();
                if (!data.erro) {
                    setUf(data.uf);

                    // Busca cidades explicitamente para garantir que a lista esteja carregada
                    // antes de setar a cidade, evitando que o select fique vazio.
                    try {
                        const respCidades = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${data.uf}/municipios`);
                        const dataCidades = await respCidades.json();
                        const listaCidades = dataCidades.map(m => m.nome).sort();
                        setCidades(listaCidades);

                        // Agora que temos a lista, podemos setar a cidade com segurança
                        setCidade(data.localidade);
                    } catch (err) {
                        console.error("Erro ao buscar cidades do IBGE:", err);
                        // Mesmo se falhar o IBGE, tenta setar a cidade (pode não aparecer no select se vazio, mas mantém o estado)
                        setCidade(data.localidade);
                    }

                    setRua(data.logradouro);
                    setBairro(data.bairro);
                    setComplemento(data.complemento);
                }
            } catch (error) { console.error("Erro ao buscar CEP:", error); }
        }
    };

    const handleCepChange = (e) => {
        const value = e.target.value;
        setCep(value);
        if (value.replace(/\D/g, '').length === 8) {
            buscarCEP(value);
        }
    };

    useEffect(() => {
        // Apenas busca se a lista estiver vazia ou se houver mudança de UF manual
        // Mas para simplificar e garantir consistência, deixamos buscar sempre que mudar UF.
        // O cache do navegador deve lidar com a repetição da requisição se for rápida.
        if (uf && uf.trim()) buscarCidadesPorUF(uf.trim());
    }, [uf, buscarCidadesPorUF]);

    useEffect(() => {
        const buscarTransportadoras = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/empresas`, { headers: { 'Authorization': `Bearer ${token}` } });
                const data = await response.json();
                setTransportadoras(data.filter(e => e.is_transportadora));
            } catch (error) { console.error("Erro ao buscar transportadoras:", error); }
        };
        if (is_cliente) buscarTransportadoras();
    }, [currentUser, is_cliente]);

    // --- Importar CNPJ Funcionalidade ---
    const handleImportCNPJ = async () => {
        const cnpjClean = cpf_cnpj.replace(/\D/g, '');
        if (!cnpjClean || cnpjClean.length !== 14) {
            Swal.fire({
                icon: 'warning',
                title: 'CNPJ Inválido',
                text: 'Por favor, informe um CNPJ válido com 14 dígitos para importar.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Importar dados da Receita?',
            text: "Ao importar os dados da receita, os dados já preenchidos serão substituidos. Deseja continuar?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2185c6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sim, importar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            setLoadingImport(true);
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                // Call backend proxy
                const response = await fetch(`${API_URL}/api/external/cnpj/${cnpjClean}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    if (response.status === 429) throw new Error("Muitas requisições. Tente novamente em alguns segundos.");
                    const errData = await response.json();
                    throw new Error(errData.error || "Erro ao consultar ReceitaWS via proxy.");
                }

                const data = await response.json();

                if (data.status === "ERROR") {
                    throw new Error(data.message || "CNPJ não encontrado na base da Receita.");
                }

                // Populate Fields
                setNome(data.nome || '');
                setNomeFantasia(data.fantasia || '');
                setEmail(data.email || '');
                setTelefone(data.telefone || '');

                // Address logic
                // const cepFormatted = data.cep ? data.cep.replace('.', '').replace('-', '') : ''; // UNUSED
                setCep(data.cep || ''); // Keep verify logic if needed, or set directly
                setUf(data.uf || '');
                setCidade(data.municipio || '');
                setRua(data.logradouro || '');
                setNumero(data.numero || '');
                setBairro(data.bairro || '');
                setComplemento(data.complemento || '');

                // Trigger cities loading if UF present (to ensure consistent state with select)
                // Normalization helper
                const normalize = (s) => s ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

                // Trigger cities loading if UF present
                if (data.uf) {
                    buscarCidadesPorUF(data.uf).then((listaCidades) => {
                        const cityFromApi = data.municipio || '';
                        const normalizedApiCity = normalize(cityFromApi);

                        // Try to find exact match (case insensitive/accent insensitive)
                        const matchedCity = listaCidades.find(c => normalize(c) === normalizedApiCity);

                        if (matchedCity) {
                            setCidade(matchedCity);
                        } else {
                            // Helper for title case if not found
                            const toTitleCase = (str) => {
                                return str.replace(
                                    /\w\S*/g,
                                    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                                );
                            };
                            setCidade(toTitleCase(cityFromApi));
                        }
                    });
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Dados Importados!',
                    text: 'Os dados foram preenchidos com sucesso.',
                    timer: 2000,
                    showConfirmButton: false
                });

            } catch (error) {
                console.error("Erro importação CNPJ:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erro na Importação',
                    text: error.message || 'Não foi possível importar os dados. Verifique o CNPJ ou tente manualmente.',
                    confirmButtonColor: '#2185c6'
                });
            } finally {
                setLoadingImport(false);
            }
        }
    };

    const [loadingImport, setLoadingImport] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();
        const formData = {
            tipo_pessoa,
            nome, nome_fantasia, cpf_cnpj, email, telefone, cep, uf, cidade, rua, numero, bairro, complemento,
            is_cliente, is_fornecedor, is_transportadora, is_terceiro,
            transportadora_padrao_id: is_cliente ? transportadora_padrao_id : null
        };
        onSubmit(formData);
    };

    return (
        <>
            <form onSubmit={handleSubmit} className="mb-5"> {/* mb-5 para dar espaço para a barra fixa */}
                <div className="row g-4">
                    {/* Coluna Principal */}
                    <div className="col-lg-8">
                        <div className="form-card">
                            <h2 className="form-section-header">Dados Básicos</h2>

                            <div className="mb-4">
                                <label className="form-label-custom mb-2 d-block">Tipo de Pessoa</label>
                                <div className="d-flex gap-4">
                                    <div className="form-check">
                                        <input className="form-check-input form-check-input-custom" type="radio" name="tipoPessoa" id="radioFisica" value="fisica" checked={tipo_pessoa === 'fisica'} onChange={(e) => setTipoPessoa(e.target.value)} />
                                        <label className="form-check-label ms-2" htmlFor="radioFisica">Pessoa Física</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input form-check-input-custom" type="radio" name="tipoPessoa" id="radioJuridica" value="juridica" checked={tipo_pessoa === 'juridica'} onChange={(e) => setTipoPessoa(e.target.value)} />
                                        <label className="form-check-label ms-2" htmlFor="radioJuridica">Pessoa Jurídica</label>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3">
                                {tipo_pessoa === 'fisica' ? (
                                    <>
                                        <div className="col-md-4">
                                            <label htmlFor="inputCpf" className="form-label-custom">CPF</label>
                                            <input onChange={(e) => setCpfCnpj(e.target.value)} type="text" className="form-control-custom" id="inputCpf" value={cpf_cnpj} maxLength="14" placeholder="000.000.000-00" />
                                        </div>
                                        <div className="col-md-8">
                                            <label htmlFor="inputNome" className="form-label-custom required-field">Nome Completo</label>
                                            <input onChange={(e) => setNome(e.target.value)} type="text" className="form-control-custom" id="inputNome" value={nome} required placeholder="Nome do cliente/fornecedor" />
                                        </div>
                                        <div className="col-md-12">
                                            <label htmlFor="inputNomeFantasia" className="form-label-custom">Apelido (Opcional)</label>
                                            <input onChange={(e) => setNomeFantasia(e.target.value)} type="text" className="form-control-custom" id="inputNomeFantasia" value={nome_fantasia} placeholder="Como a empresa é conhecida" />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="col-md-4">
                                            <label htmlFor="inputCnpj" className="form-label-custom">CNPJ</label>
                                            <div className="input-group">
                                                <input
                                                    onChange={(e) => setCpfCnpj(e.target.value)}
                                                    type="text"
                                                    className="form-control-custom"
                                                    id="inputCnpj"
                                                    value={cpf_cnpj}
                                                    maxLength="18"
                                                    placeholder="00.000.000/0000-00"
                                                />
                                                <button
                                                    className="btn btn-primary-custom"
                                                    type="button"
                                                    onClick={handleImportCNPJ}
                                                    disabled={loadingImport}
                                                    title="Importar dados da receita"
                                                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, height: '42px', width: '42px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    {loadingImport ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <i className="fas fa-cloud-download-alt"></i>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <label htmlFor="inputRazaoSocial" className="form-label-custom required-field">Razão Social</label>
                                            <input onChange={(e) => setNome(e.target.value)} type="text" className="form-control-custom" id="inputRazaoSocial" value={nome} required placeholder="Razão Social da empresa" />
                                        </div>
                                        <div className="col-md-12">
                                            <label htmlFor="inputNomeFantasia" className="form-label-custom">Nome Fantasia</label>
                                            <input onChange={(e) => setNomeFantasia(e.target.value)} type="text" className="form-control-custom" id="inputNomeFantasia" value={nome_fantasia} placeholder="Nome comercial" />
                                        </div>
                                    </>
                                )}
                            </div>

                            {is_cliente && (
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <label htmlFor="inputTransportadora" className="form-label-custom">Transportadora Padrão</label>
                                        <select id="inputTransportadora" className="form-select-custom" value={transportadora_padrao_id} onChange={(e) => setTransportadoraPadraoId(e.target.value)}>
                                            <option value="">Selecione...</option>
                                            {transportadoras.map((t) => (<option key={t.id} value={t.id}>{t.nome}</option>))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-card mt-4">
                            <h2 className="form-section-header">Endereço</h2>
                            <div className="row g-3">

                                <div className="col-md-3">
                                    <label htmlFor="inputCep" className="form-label-custom">CEP</label>
                                    <input onChange={handleCepChange} onBlur={(e) => buscarCEP(e.target.value)} type="text" className="form-control-custom" id="inputCep" value={cep} maxLength="9" placeholder="00000-000" />
                                </div>

                            </div>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label htmlFor="inputUf" className="form-label-custom">UF</label>
                                    <select id="inputUf" className="form-select-custom" value={uf} onChange={(e) => setUf(e.target.value)}>
                                        <option value="">UF</option>
                                        {UFs.map((estado) => (<option key={estado} value={estado}>{estado}</option>))}
                                    </select>
                                </div>


                                <div className="col-md-8">
                                    <label htmlFor="inputCidade" className="form-label-custom">Cidade</label>
                                    <select id="inputCidade" className="form-select-custom" value={cidade} onChange={(e) => setCidade(e.target.value)} disabled={!uf}>
                                        <option value="">{uf ? 'Selecione...' : 'Selecione um UF'}</option>
                                        {cidades.map((c) => (<option key={c} value={c}>{c}</option>))}
                                    </select>
                                </div>

                                <div className="col-md-9">
                                    <label htmlFor="inputRua" className="form-label-custom">Logradouro</label>
                                    <input onChange={(e) => setRua(e.target.value)} type="text" className="form-control-custom" id="inputRua" value={rua} />
                                </div>
                                <div className="col-md-3">
                                    <label htmlFor="inputNumero" className="form-label-custom">Número</label>
                                    <input onChange={(e) => setNumero(e.target.value)} type="text" className="form-control-custom" id="inputNumero" value={numero} />
                                </div>

                                <div className="col-md-6">
                                    <label htmlFor="inputBairro" className="form-label-custom">Bairro</label>
                                    <input onChange={(e) => setBairro(e.target.value)} type="text" className="form-control-custom" id="inputBairro" value={bairro} />
                                </div>
                                <div className="col-md-6">
                                    <label htmlFor="inputComplemento" className="form-label-custom">Complemento</label>
                                    <input onChange={(e) => setComplemento(e.target.value)} type="text" className="form-control-custom" id="inputComplemento" value={complemento} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Coluna Lateral */}
                    <div className="col-lg-4">
                        <div className="form-card h-100">
                            <h2 className="form-section-header">Categorias</h2>
                            <div className="d-flex flex-column gap-3 mb-4">
                                <div className="d-flex align-items-center bg-light p-3 rounded border">
                                    <input className="form-check-input form-check-input-custom" type="checkbox" id="checkCliente" checked={is_cliente} onChange={(e) => setIsCliente(e.target.checked)} />
                                    <label className="form-check-label ms-2 fw-medium mb-0" htmlFor="checkCliente">Cliente</label>
                                </div>
                                <div className="d-flex align-items-center bg-light p-3 rounded border">
                                    <input className="form-check-input form-check-input-custom" type="checkbox" id="checkFornecedor" checked={is_fornecedor} onChange={(e) => setIsFornecedor(e.target.checked)} />
                                    <label className="form-check-label ms-2 fw-medium mb-0" htmlFor="checkFornecedor">Fornecedor</label>
                                </div>
                                <div className="d-flex align-items-center bg-light p-3 rounded border">
                                    <input className="form-check-input form-check-input-custom" type="checkbox" id="checkTransportador" checked={is_transportadora} onChange={(e) => setIsTransportadora(e.target.checked)} />
                                    <label className="form-check-label ms-2 fw-medium mb-0" htmlFor="checkTransportador">Transportadora</label>
                                </div>
                                <div className="d-flex align-items-center bg-light p-3 rounded border">
                                    <input className="form-check-input form-check-input-custom" type="checkbox" id="checkTerceiro" checked={is_terceiro} onChange={(e) => setIsTerceiro(e.target.checked)} />
                                    <label className="form-check-label ms-2 fw-medium mb-0" htmlFor="checkTerceiro">Terceiro</label>
                                </div>
                            </div>

                            <hr className="my-4" />

                            <h2 className="form-section-header">Contato</h2>
                            <div className="mb-3">
                                <label htmlFor="inputEmail" className="form-label-custom">E-mail</label>
                                <input onChange={(e) => setEmail(e.target.value)} type="email" className="form-control-custom" id="inputEmail" value={email} placeholder="email@exemplo.com" />
                            </div>
                            <div className="mb-3">
                                <label htmlFor="inputTelefone" className="form-label-custom">Telefone</label>
                                <input onChange={(e) => setTelefone(e.target.value)} type="tel" className="form-control-custom" id="inputTelefone" value={telefone} placeholder="(00) 00000-0000" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Action Bar */}
                <div className="form-actions-bar">
                    <div className="container-fluid d-flex justify-content-end gap-3 align-items-center">
                        <Link to='/app/empresas' className="btn btn-secondary-custom">
                            Cancelar
                        </Link>
                        <button type="submit" className="btn btn-primary-custom" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditing ? 'Atualizando...' : 'Salvando...'}
                                </>
                            ) : (
                                isEditing ? 'Atualizar Empresa' : 'Salvar Empresa'
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </>
    );
}

export default EmpresaForm;